/**
 * Configurator Height Measurement
 * Measures the height of the .product-configurator element and sets it as a CSS custom property
 * on the body element for responsive layout calculations.
 */

class ConfiguratorHeightMeasurer {
  constructor() {
    /** @type {HTMLElement | null} */
    this.configurator = null;
    /** @type {ResizeObserver | null} */
    this.observer = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.configurator = /** @type {HTMLElement} */ (document.querySelector('.product-configurator'));

    if (!this.configurator) {
      console.warn('ConfiguratorHeightMeasurer: Required configurator element not found');
      return;
    }

    // Measure height immediately
    this.measureAndSetHeight();

    // Set up ResizeObserver to handle dynamic height changes
    this.setupResizeObserver();

    // Also listen for window resize events as a fallback
    window.addEventListener(
      'resize',
      this.debounce(() => {
        this.measureAndSetHeight();
      }, 100)
    );
  }

  measureAndSetHeight() {
    if (!this.configurator) return;

    // Get the actual height of the configurator
    const height = this.configurator.offsetHeight;

    if (height > 0) {
      // Set the CSS custom property on the body element
      document.body.style.setProperty('--product-configurator-height', `${height}px`);

      // Dispatch a custom event for other scripts that might need this value
      document.body.dispatchEvent(
        new CustomEvent('configurator-height-updated', {
          detail: { height },
        })
      );
    }

    // Measure and set the footer plus height variable
    this.measureAndSetFooterPlusHeight();
  }

  measureAndSetFooterPlusHeight() {
    if (!this.configurator) return;

    // Find the product config footer element
    const footer = /** @type {HTMLElement | null} */ (document.querySelector('.product-config-footer'));
    if (!footer) {
      console.warn('ConfiguratorHeightMeasurer: Product config footer element not found');
      return;
    }

    // Get the footer height
    const footerHeight = footer.offsetHeight;

    // Get the computed styles of the configurator to calculate padding bottom
    const configuratorStyles = window.getComputedStyle(this.configurator);
    const configuratorPaddingBottom = parseFloat(configuratorStyles.paddingBottom) || 0;

    // Calculate the total height: footer height + (configurator padding bottom * 2)
    const footerPlusHeight = footerHeight + configuratorPaddingBottom * 2;

    // Set the CSS custom property on the product-configurator element
    this.configurator.style.setProperty('--product-conf-footer-plus-height', `${footerPlusHeight}px`);

    // Dispatch a custom event for other scripts that might need this value
    this.configurator.dispatchEvent(
      new CustomEvent('configurator-footer-plus-height-updated', {
        detail: {
          footerHeight,
          configuratorPaddingBottom,
          footerPlusHeight,
        },
      })
    );
  }

  setupResizeObserver() {
    if (!window.ResizeObserver) {
      console.warn('ConfiguratorHeightMeasurer: ResizeObserver not supported');
      return;
    }

    this.observer = new ResizeObserver(
      this.debounce((/** @type {ResizeObserverEntry[]} */ entries) => {
        for (const entry of entries) {
          if (entry.target === this.configurator) {
            this.measureAndSetHeight();
          } else if (entry.target.classList.contains('product-config-footer')) {
            // If footer changes, update the footer plus height
            this.measureAndSetFooterPlusHeight();
          }
        }
      }, 50)
    );

    if (this.configurator) {
      this.observer.observe(this.configurator);
    }

    // Also observe the footer element if it exists
    const footer = document.querySelector('.product-config-footer');
    if (footer) {
      this.observer.observe(footer);
    }
  }

  // Utility function to debounce function calls
  /**
   * @param {Function} func
   * @param {number} wait
   */
  debounce(func, wait) {
    /** @type {number | undefined} */
    let timeout;
    return function executedFunction(/** @type {any[]} */ ...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Cleanup method
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    // Note: We can't easily remove the debounced resize listener without storing a reference
    // This is acceptable for this use case as the page will be unloaded anyway
  }
}

// Initialize the measurer
new ConfiguratorHeightMeasurer();
